import restClient from '@common/configs/api';
import { AppIpResource, IAppInfo } from '@common/types/element';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Per-app IP restriction gate for the generated app (MIK-4882).
 *
 * The verdict is computed server-side by `app-ip-restriction-evaluate`, which
 * returns booleans only; this hook only decides which surface to ask about.
 *
 * The canvas preview modal is exempt here: it loads the same /preview/:appId
 * document, but the builder has already gated it, so it carries a marker and
 * the runtime leaves it alone. The allow-list itself never reaches the client: it
 * names every network the app trusts, and the app document is public.
 *
 * SECURITY: this is still a client-side gate. It stops the app UI from
 * rendering on a disallowed network, but the backend API remains reachable
 * directly. Server-side enforcement is tracked separately.
 *
 * Availability beats strictness: if the verdict cannot be fetched the gate
 * fails OPEN rather than taking the whole app down. An app with the flag off
 * short-circuits before any network call, so it pays nothing.
 */

/** Routes served as the `preview` surface when opened directly. */
const PREVIEW_PATH_PREFIXES = ['/preview/', '/preview-v4/'];

/** True when this document is embedded in an iframe. */
const isEmbedded = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent: the access throws, which itself means we are framed.
    return true;
  }
};

/**
 * Which surface this document is.
 *
 * Every published surface - /share-app/:appId, /share-modal/:appId, / and the
 * custom-domain pages - renders the app in an IFRAME pointing at
 * /preview/:appId. Twelve call sites build that URL. So the iframe's own
 * pathname says `preview` even when the visitor is looking at the published
 * app, and keying off it alone gated the whole published site as `preview`.
 *
 * Being framed is the reliable signal instead:
 *   - framed, and not the canvas modal  -> a published surface  -> `production`
 *   - not framed, path starts /preview/ -> the preview URL       -> `preview`
 *   - anything else                     -> `production`
 *
 * The canvas preview modal is also framed, but it carries `canvasPreview=1`
 * and is skipped before this runs - the builder gates it on `preview` itself.
 */
const getCurrentIpResource = (): AppIpResource => {
  if (Platform.OS !== 'web') return 'production';
  if (isEmbedded()) return 'production';
  const pathname =
    typeof window !== 'undefined' ? window.location?.pathname ?? '' : '';
  return PREVIEW_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
    ? 'preview'
    : 'production';
};

/**
 * True when this document is the canvas preview modal's iframe.
 *
 * That surface is gated in the builder on `preview`, where the context is
 * known for certain, so the runtime must not gate it again as `production` -
 * otherwise blocking the published app would also break the canvas preview.
 *
 * This is a client-supplied marker and is therefore only trustworthy for a
 * client-side gate. Server-side enforcement must never key off it.
 */
const isCanvasPreview = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location?.search ?? '').get(
        'canvasPreview'
      ) === '1'
    );
  } catch {
    return false;
  }
};

type EvaluateResult = {
  enabled: boolean;
  allowed?: Partial<Record<AppIpResource, boolean>>;
};

const fetchVerdict = async (appId: string): Promise<EvaluateResult> => {
  const res = await restClient.get<EvaluateResult>(
    '/app-ip-restriction-evaluate',
    { params: { appId }, timeout: 15000 }
  );
  return res.data;
};

export type IpRestrictionState = {
  /** True while the verdict is still being fetched - render a loader. */
  checking: boolean;
  /** False only when the restriction is on and this surface is not granted. */
  allowed: boolean;
};

export const useIpRestriction = (
  appInfo?: Pick<IAppInfo, 'ipRestriction' | 'appId' | 'id'> | null
): IpRestrictionState => {
  const active = appInfo?.ipRestriction?.enabled === true;
  const appId = (appInfo?.appId || appInfo?.id || '') as string;

  const gated = active && !isCanvasPreview();
  const [state, setState] = useState<IpRestrictionState>({
    checking: gated,
    allowed: !gated,
  });

  useEffect(() => {
    if (!active || !appId || isCanvasPreview()) {
      setState({ checking: false, allowed: true });
      return;
    }

    let cancelled = false;
    setState({ checking: true, allowed: false });

    fetchVerdict(appId)
      .then((verdict) => {
        if (cancelled) return;
        setState({
          checking: false,
          allowed:
            !verdict?.enabled ||
            verdict.allowed?.[getCurrentIpResource()] !== false,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        // Fail open: a transient lookup failure must not black out the app.
        // eslint-disable-next-line no-console
        console.warn('[ipRestriction] could not resolve the verdict', error);
        setState({ checking: false, allowed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [active, appId]);

  return state;
};
